import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// VPS (Vendor Process Sheet) — AS PER MAX buyer checklist. 4 stage cards with
// 34 checkpoints; auto-linked items pull from Quality/GRN/test/PO records.

const API_ORIGIN = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
const fileHref = (u) => !u ? '#' : (/^https?:\/\//.test(u) ? u : (u.startsWith('/api/') ? API_ORIGIN + u : u));
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

const STAGES = [
  { key: 'size_set', label: '1 · Size Set', icon: '📏' },
  { key: 'inline', label: '2 · Inline', icon: '🧵' },
  { key: 'midline', label: '3 · Midline', icon: '📦' },
  { key: 'final', label: '4 · Final Report', icon: '🚚' },
];
const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, boxSizing: 'border-box' };
const labelStyle = { fontSize: 9, color: '#888', fontWeight: 600, display: 'block', marginBottom: 2, textTransform: 'uppercase' };

function Checkpoint({ cp, gpoNo, onChanged, canEdit }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const complete = cp.status === 'complete';
  const na = cp.status === 'na';

  const patch = async (body) => {
    setBusy(true);
    try { await api.patch(`/oe/vps/${encodeURIComponent(gpoNo)}/${cp.checkpoint_key}`, body); setOpen(false); setForm({}); onChanged(); }
    catch (e) { alert(e.response?.data?.error || 'Update failed'); }
    finally { setBusy(false); }
  };
  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/files/vps-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm({ ...form, document_url: r.data.url, document_name: f.name });
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    finally { setBusy(false); e.target.value = ''; }
  };

  return (
    <div style={{
      border: `1px solid ${complete ? '#bbe5c5' : na ? '#e5e7eb' : '#eee'}`, borderRadius: 8,
      padding: '8px 10px', marginBottom: 6, fontSize: 11,
      background: complete ? '#EAF7EE' : na ? '#F7F6F3' : 'white', opacity: na ? 0.65 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, cursor: canEdit && !complete && !na ? 'pointer' : 'default' }}
        onClick={() => canEdit && !complete && !na && setOpen(o => !o)}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 11 }}>{cp.checkpoint_name}</div>
          {cp.auto_source && <span style={{ fontSize: 9, color: '#3B3A8F', fontWeight: 700 }}>🔗 Auto-linked · {cp.auto_source}</span>}
          {complete && <div style={{ fontSize: 10, color: '#166534', marginTop: 2 }}>✓ {fmtD(cp.actual_date)}{cp.document_name ? ` · ${cp.document_name}` : ''}</div>}
          {cp.remarks && <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>{cp.remarks}</div>}
          {cp.document_url && <a href={fileHref(cp.document_url)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 10, color: '#3B3A8F', fontWeight: 700 }}>👁 View</a>}
        </div>
        <span style={{
          padding: '2px 8px', borderRadius: 8, fontSize: 9, fontWeight: 800, whiteSpace: 'nowrap',
          background: complete ? '#16653422' : na ? '#88888822' : '#9A6B0022',
          color: complete ? '#166534' : na ? '#888' : '#9A6B00' }}>{complete ? 'COMPLETE' : na ? 'N/A' : 'PENDING'}</span>
      </div>

      {open && canEdit && !complete && !na && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #eee' }} onClick={e => e.stopPropagation()}>
          {!cp.date_only && (
            <div style={{ marginBottom: 6 }}>
              <label style={{ padding: '4px 10px', background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 700, display: 'inline-block' }}>
                {busy ? 'Uploading…' : form.document_url ? '✅ ' + (form.document_name || 'uploaded') : '📎 Upload document' + (cp.requires_doc ? ' *' : '')}
                <input type="file" accept="image/*,.pdf,.xlsx,.xls" style={{ display: 'none' }} onChange={upload} />
              </label>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <div><label style={labelStyle}>Actual Date</label>
              <input type="date" style={inputStyle} value={form.actual_date || ''} onChange={e => setForm({ ...form, actual_date: e.target.value })} /></div>
            <div><label style={labelStyle}>Planned Date</label>
              <input type="date" style={inputStyle} value={form.planned_date || (cp.planned_date ? String(cp.planned_date).slice(0, 10) : '')} onChange={e => setForm({ ...form, planned_date: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 6 }}><label style={labelStyle}>Remarks{cp.checkpoint_key === 'shipment_sample' ? ' (courier name + tracking no)' : ''}</label>
            <input style={inputStyle} value={form.remarks || ''} onChange={e => setForm({ ...form, remarks: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
            <button disabled={busy} onClick={() => patch({ ...form, status: 'na' })}
              style={{ padding: '4px 10px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 5, fontSize: 10, cursor: 'pointer' }}>N/A</button>
            <button disabled={busy} onClick={() => patch(form)}
              style={{ padding: '4px 10px', background: '#EEEDF8', color: '#3B3A8F', border: 'none', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 700 }}>💾 Save</button>
            <button disabled={busy} onClick={() => patch({ ...form, status: 'complete' })}
              style={{ padding: '4px 12px', background: '#166534', color: 'white', border: 'none', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 700 }}>✓ Mark Complete</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VPSTab({ gpos, user }) {
  const [gpoNo, setGpoNo] = useState('');
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState(null);
  const canEdit = ['admin', 'management', 'merchandiser'].includes(user?.role);
  const activeGpos = (gpos || []).filter(g => !g.is_superseded);

  const load = useCallback(async () => {
    if (!gpoNo) { setRows([]); setProgress(null); return; }
    const r = await api.get('/oe/vps/' + encodeURIComponent(gpoNo)).catch(() => null);
    if (r) { setRows(r.data.data || []); setProgress(r.data.progress); }
  }, [gpoNo]);
  useEffect(() => { load(); }, [load]);

  const barColor = (pct) => pct >= 67 ? '#166534' : pct >= 34 ? '#f59e0b' : '#E22D2D';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 280 }}>
          <label style={{ ...labelStyle, fontSize: 10 }}>Select GPO</label>
          <select style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }} value={gpoNo} onChange={e => setGpoNo(e.target.value)}>
            <option value="">— choose a GPO —</option>
            {activeGpos.map(g => <option key={g.id} value={g.gpo_no}>{g.gpo_no} · {g.style_no || ''} {g.brand ? '· ' + g.brand : ''}</option>)}
          </select>
        </div>
        {progress && (
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
              <span style={{ fontWeight: 800, color: '#3B3A8F' }}>VPS PROGRESS (AS PER MAX CHECKLIST)</span>
              <span style={{ fontWeight: 800, color: barColor(progress.pct) }}>{progress.done}/{progress.total} · {progress.pct}%</span>
            </div>
            <div style={{ background: '#eee', borderRadius: 6, height: 12, overflow: 'hidden' }}>
              <div style={{ width: progress.pct + '%', height: '100%', background: barColor(progress.pct), transition: 'width .3s' }} />
            </div>
          </div>
        )}
      </div>

      {!gpoNo ? (
        <div style={{ padding: '10px 14px', background: '#EEEDF8', borderRadius: 6, fontSize: 13, color: '#3B3A8F' }}>
          📋 VPS — Vendor Process Sheet (AS PER MAX). 4 stages · 34 checkpoints. Auto-linked items complete themselves from
          Quality (shrinkage / 4-point), Test Reports (FPT/GPT) and Purchase Orders. Completions auto-publish the matching T&A activity.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          {STAGES.map(s => {
            const list = rows.filter(r => r.stage === s.key);
            const done = list.filter(r => r.status === 'complete' || r.status === 'na').length;
            const stageDone = list.length > 0 && done === list.length;
            return (
              <div key={s.key} style={{ border: `2px solid ${stageDone ? '#16653455' : '#eee'}`, borderRadius: 10, background: '#fafafa', overflow: 'hidden' }}>
                <div style={{ padding: '10px 12px', background: stageDone ? '#EAF7EE' : '#1a1a2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 800, fontSize: 12, color: stageDone ? '#166534' : 'white' }}>{s.icon} {s.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, color: stageDone ? '#166534' : '#aaa' }}>{done}/{list.length}{stageDone ? ' ✓' : ''}</span>
                </div>
                <div style={{ padding: 8 }}>
                  {list.map(cp => <Checkpoint key={cp.checkpoint_key} cp={cp} gpoNo={gpoNo} onChanged={load} canEdit={canEdit} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
